import React from "react";
import { Condition, Element, SubmitButton } from "@shared/types";
import { FormWrapper, NextButtonContent } from "../form-wrapper";
import { useForm, useWatch } from "react-hook-form";
import { YesNoQuestion } from "./yes-no-question";
import { Position } from "../question";
import { TextQuestion } from "./text-question";
import { SliderQuestion } from "./slider-question";
import { SingleSelectBoxQuestion } from "./single-select-box-question";
import { MultipleSelectBoxQuestion } from "./multiple-select-box-question";
import { TextAreaQuestion } from "./text-area-question";
import { exhaustivenessCheck } from "@/utils/exhaustiveness-check";
import classNames from "classnames";
import { Navigate, useNavigate } from "react-router-dom";
import { useFormState } from "../context/hooks/useFormState";
import isEqual from "lodash/isEqual";
import { ControlledFaceComplaintsMap } from "../form/controlled-face-complaints-map";
import { ControlledBodyComplaintsMap } from "../form/controlled-body-complaints-map";
import { ComplaintsMapIntro } from "./complaints-map-intro";
import { useTranslation } from "react-i18next";

type DynamicFormContainerProps = {
  elements: Element[];
  showDivider: boolean;
  nextRoute: string;
  submitButton?: SubmitButton;
};

export const DynamicFormPage: React.FC<DynamicFormContainerProps> = ({
  elements,
  showDivider,
  nextRoute,
  submitButton,
}) => {
  const navigate = useNavigate();
  const { formState: contextFormState, updateFormState } = useFormState();
  const { i18n, t } = useTranslation();

  const defaultValues = elements.reduce((prev, element) => {
    if (element.type === "complaints-map-intro") {
      return prev;
    }

    return {
      ...prev,
      ...(element.type === "single-select-box"
        ? {
            [element.id]: null,
          }
        : {}),
      ...(element.type === "multiple-select-box"
        ? {
            [element.id]: [],
          }
        : {}),
      ...(element.type === "slider"
        ? {
            [element.id]: element.options.default,
          }
        : {}),
      ...(element.type === "text" || element.type === "text-area"
        ? {
            [element.id]: "",
          }
        : {}),
      ...(element.type === "face-complaints-map" ||
      element.type === "body-complaints-map"
        ? {
            [element.id]: { front: [], back: [], left: [], right: [] },
          }
        : {}),
      ...(contextFormState[element.id] !== undefined
        ? {
            [element.id]: contextFormState[element.id],
          }
        : {}),
    };
  }, {});

  const { handleSubmit, control } = useForm({
    defaultValues,
  });

  const watchedValues = useWatch<Record<string, unknown>>({ control });

  const isElementIdOnCurrentPage = (elementId: string) => {
    return elements.some((element) => element.id === elementId);
  };

  const compareValues = (formFieldValue: unknown, conditionValue: unknown) => {
    // The formField can be an array (multiple-select-box)
    if (Array.isArray(formFieldValue)) {
      return formFieldValue.includes(conditionValue);
    }
    return formFieldValue === conditionValue;
  };

  const checkIfConditionMet = (condition: Condition) => {
    const conditionalElement = isElementIdOnCurrentPage(condition.field)
      ? watchedValues
      : contextFormState;

    const formFieldValue = conditionalElement[condition.field];

    if (condition.negotiation) {
      return !compareValues(formFieldValue, condition.value);
    }

    return compareValues(formFieldValue, condition.value);
  };

  const displayedElements = elements.filter((element) => {
    if (element.show_if_every) {
      return element.show_if_every.every(checkIfConditionMet);
    }
    if (element.show_if_some) {
      return element.show_if_some.some(checkIfConditionMet);
    }
    return true;
  });

  if (displayedElements.length === 0) {
    return <Navigate to={nextRoute} replace />;
  }

  const onSubmit = (data: Record<string, unknown>) => {
    updateFormState(data);
    navigate(nextRoute);
  };

  const createElement = (element: Element, index: number) => {
    const position = ((): Position | undefined => {
      if (displayedElements.length === 1) return;
      if (index === 0) return "first";
      if (index === displayedElements.length - 1) return "last";

      return "middle";
    })();

    if (element.type === "yes-no") {
      return (
        <YesNoQuestion
          key={index}
          question={element.question}
          control={control}
          position={position}
          id={element.id}
          required={
            element.required === true
              ? t("common.validation.required")
              : undefined
          }
        />
      );
    }

    if (element.type === "text") {
      return (
        <TextQuestion
          key={index}
          control={control}
          question={element.question}
          id={element.id}
          position={position}
          required={
            element.required === true
              ? t("common.validation.required")
              : undefined
          }
        />
      );
    }

    if (element.type === "text-area") {
      return (
        <TextAreaQuestion
          key={index}
          rows={element.rows}
          control={control}
          question={element.question}
          id={element.id}
          position={position}
          required={
            element.required === true
              ? t("common.validation.required")
              : undefined
          }
        />
      );
    }

    if (element.type === "slider") {
      return (
        <SliderQuestion
          key={index}
          control={control}
          id={element.id}
          options={element.options}
          position={position}
          question={element.question}
        />
      );
    }

    if (
      element.type === "single-select-box" ||
      element.type === "multiple-select-box"
    ) {
      const answerOptions = Object.fromEntries(
        Object.entries(element.answer_options).filter(([key]) => {
          if (!element.answer_options[key].show_if_every) {
            return true;
          }
          return checkIfConditionMet(element.answer_options[key].show_if_every);
        }),
      );

      if (element.type === "multiple-select-box") {
        return (
          <MultipleSelectBoxQuestion
            key={index}
            control={control}
            id={element.id}
            answer_options={answerOptions}
            position={position}
            question={element.question}
            required={
              element.required === true
                ? t("common.validation.required")
                : undefined
            }
          />
        );
      }

      return (
        <SingleSelectBoxQuestion
          key={index}
          control={control}
          id={element.id}
          answer_options={answerOptions}
          position={position}
          question={element.question}
          required={
            element.required === true
              ? t("common.validation.required")
              : undefined
          }
        />
      );
    }

    if (element.type === "face-complaints-map") {
      return (
        <ControlledFaceComplaintsMap
          key={index}
          control={control}
          gender={contextFormState.gender}
          name={element.id}
        />
      );
    }

    if (element.type === "body-complaints-map") {
      return (
        <ControlledBodyComplaintsMap
          key={index}
          control={control}
          gender={contextFormState.gender}
          name={element.id}
        />
      );
    }

    if (element.type === "complaints-map-intro") {
      return <ComplaintsMapIntro key={index} />;
    }

    exhaustivenessCheck(element);
    return <></>;
  };

  const SubmitButton: React.FC = () => {
    if (!submitButton) {
      return <NextButtonContent />;
    }

    if (
      isEqual(
        watchedValues[submitButton.condition.field],
        submitButton.condition.value,
      )
    ) {
      return <NextButtonContent text={submitButton.text[i18n.language]} />;
    }

    return <NextButtonContent />;
  };

  return (
    <FormWrapper
      onSubmit={handleSubmit(onSubmit)}
      SubmitButtonContent={SubmitButton}
    >
      <div
        className={classNames("flex flex-col mb-14", {
          "divide-y divide-gray-300": showDivider,
        })}
      >
        {displayedElements.map(createElement)}
      </div>
    </FormWrapper>
  );
};
